import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Gift, Search, Coins } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TablePagination } from '@/components/ui/pagination';
import { Spinner } from '@/components/ui/spinner';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { ReferralSettingsCard } from '../components/ReferralSettingsCard';

// ─── Types ───────────────────────────────────────────────────────────────────

type ReferralStatus = 'pending' | 'rewarded' | 'cancelled';

interface Party {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
}

interface Referral {
  id: string;
  code: string;
  status: ReferralStatus;
  referee_coins: number;
  referrer_coins: number;
  qualifying_booking_id: string | null;
  rewarded_at: string | null;
  created_at: string;
  referrer: Party;
  referee: Party;
}

interface ReferralListResponse {
  referrals: Referral[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ReferralStats {
  total_referrals: number;
  pending: number;
  rewarded: number;
  total_coins_distributed: number;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

const fetchStats = async (): Promise<ReferralStats> => {
  const { data } = await api.get(API_ENDPOINTS.REFERRALS.STATS);
  return data.data ?? data;
};

const fetchReferrals = async (params: {
  status: string;
  search: string;
  page: number;
  limit: number;
}): Promise<ReferralListResponse> => {
  const { data } = await api.get(API_ENDPOINTS.REFERRALS.LIST, {
    params: {
      status: params.status,
      ...(params.search ? { search: params.search } : {}),
      page: params.page,
      limit: params.limit,
    },
  });
  const payload = data.data ?? data;
  return {
    referrals: Array.isArray(payload?.referrals) ? payload.referrals : [],
    total: payload?.total ?? 0,
    page: payload?.page ?? 1,
    limit: payload?.limit ?? params.limit,
    totalPages: payload?.totalPages ?? 1,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<ReferralStatus, 'warning' | 'success' | 'secondary'> = {
  pending: 'warning',
  rewarded: 'success',
  cancelled: 'secondary',
};

const STATUS_LABEL: Record<ReferralStatus, string> = {
  pending: 'Pending',
  rewarded: 'Rewarded',
  cancelled: 'Cancelled',
};

const fmtDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

function PartyCell({ party }: { party: Party }) {
  return (
    <div className="min-w-0">
      <p className="font-medium text-sm truncate">{party?.name || 'New user'}</p>
      <p className="text-xs text-gray-500 truncate">{party?.phone || party?.email || '—'}</p>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ReferralsPage() {
  const [status, setStatus] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const { data: stats } = useQuery({ queryKey: ['referral-stats'], queryFn: fetchStats });

  const { data, isLoading } = useQuery({
    queryKey: ['referrals', status, search, page, limit],
    queryFn: () => fetchReferrals({ status, search, page, limit }),
  });

  const referrals = data?.referrals ?? [];

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const statCards = [
    { label: 'Total Referrals', value: stats?.total_referrals ?? 0, color: 'text-blue-600' },
    { label: 'Pending', value: stats?.pending ?? 0, color: 'text-amber-600' },
    { label: 'Rewarded', value: stats?.rewarded ?? 0, color: 'text-green-600' },
    { label: 'Coins Distributed', value: stats?.total_coins_distributed ?? 0, color: 'text-purple-600' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Referrals"
        description="Track who referred whom and the coins each side earned"
      />

      <ReferralSettingsCard />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(({ label, value, color }) => (
          <Card key={label}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={submitSearch} className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, phone or code…"
            className="pl-9"
          />
        </form>
        <Select
          value={status}
          onChange={(v) => { setStatus(v); setPage(1); }}
          className="sm:w-48"
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'pending', label: 'Pending' },
            { value: 'rewarded', label: 'Rewarded' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center p-12"><Spinner /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Referrer</TableHead>
                  <TableHead>Referee (New user)</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Reward</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Rewarded</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                      <Gift className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      No referrals found.
                    </TableCell>
                  </TableRow>
                )}
                {referrals.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell><PartyCell party={r.referrer} /></TableCell>
                    <TableCell><PartyCell party={r.referee} /></TableCell>
                    <TableCell>
                      <span className="font-mono font-semibold text-blue-600">{r.code}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Coins className="h-3.5 w-3.5 text-amber-500" />
                        <span className="font-medium text-purple-700">{r.referrer_coins}</span>
                        <span className="text-gray-400">/</span>
                        <span className="font-medium text-green-700">{r.referee_coins}</span>
                      </div>
                      <p className="text-[10px] text-gray-400">referrer / referee</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-sm text-gray-600">{fmtDate(r.rewarded_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > 0 && (
        <TablePagination
          currentPage={page}
          totalPages={data.totalPages}
          totalItems={data.total}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setLimit(size); setPage(1); }}
        />
      )}
    </div>
  );
}
